import {
  type BookShelvesDto,
  SHELF_BOOK_SORTS,
  type ShelfAddResultDto,
  type ShelfBooksDto,
  type ShelfDetailDto,
  type ShelfListDto,
  type SmartShelfCriteria,
} from "@shelflist/shared";
import { queryOptions } from "@tanstack/react-query";
import { z } from "zod";
import { ApiError, api, call } from "./api";
import { type BookSearch, bookSearchSchema, listQuery } from "./catalog";

/** Maximum number of books selectable at once in the catalog (the server enforces it as well). */
export const SELECTION_MAX = 500;

/** Search params of a shelf page: the catalog's plus the shelf sorts. */
export const shelfSearchSchema = bookSearchSchema.extend({
  sort: z.enum(SHELF_BOOK_SORTS).optional().catch(undefined),
});
export type ShelfSearch = z.infer<typeof shelfSearchSchema>;

export const shelfKeys = {
  all: ["shelves"] as const,
  list: ["shelves", "list"] as const,
  detail: (id: string) => ["shelves", "detail", id] as const,
  books: (id: string, search: ShelfSearch) => ["shelves", "books", id, search] as const,
  booksOf: (id: string) => ["shelves", "books", id] as const,
  forBook: (bookId: string) => ["shelves", "book", bookId] as const,
};

const notFoundNoRetry = (count: number, error: unknown) =>
  !(error instanceof ApiError && error.status === 404) && count < 2;

export const shelvesQuery = queryOptions({
  queryKey: shelfKeys.list,
  queryFn: () => call(api.shelves.$get()) as Promise<ShelfListDto>,
});

export const shelfQuery = (id: string) =>
  queryOptions({
    queryKey: shelfKeys.detail(id),
    queryFn: () => call(api.shelves[":id"].$get({ param: { id } })) as Promise<ShelfDetailDto>,
    retry: notFoundNoRetry,
  });

export const shelfBooksQuery = (id: string, search: ShelfSearch) =>
  queryOptions({
    queryKey: shelfKeys.books(id, search),
    queryFn: () =>
      call(
        api.shelves[":id"].books.$get({
          param: { id },
          query: listQuery(search as BookSearch) as never,
        }),
      ) as Promise<ShelfBooksDto>,
    placeholderData: (previous) => previous,
    retry: notFoundNoRetry,
  });

export const bookShelvesQuery = (bookId: string) =>
  queryOptions({
    queryKey: shelfKeys.forBook(bookId),
    queryFn: () =>
      call(api.books[":id"].shelves.$get({ param: { id: bookId } })) as Promise<BookShelvesDto>,
  });

export function addBooksToShelf(shelfId: string, bookIds: string[]) {
  return call(
    api.shelves[":id"].books.$post({ param: { id: shelfId }, json: { bookIds } }),
  ) as Promise<ShelfAddResultDto>;
}

export function removeBooksFromShelf(shelfId: string, bookIds: string[]) {
  return call(api.shelves[":id"].books.$delete({ param: { id: shelfId }, json: { bookIds } }));
}

export function createShelfRequest(input: {
  name: string;
  description?: string;
  visibility: "private" | "shared";
  kind?: "manual" | "smart";
  criteria?: SmartShelfCriteria;
}) {
  return call(api.shelves.$post({ json: input })) as Promise<ShelfDetailDto>;
}

export function moveBookRequest(shelfId: string, bookId: string, position: number) {
  return call(
    api.shelves[":id"].books[":bookId"].$patch({
      param: { id: shelfId, bookId },
      json: { position },
    }),
  );
}

/** Whether the criteria use per-user data, evaluated for each viewer (refine-round2 D1). */
export function hasPersonalCriteria(criteria: SmartShelfCriteria | null | undefined): boolean {
  return Boolean(criteria?.myStatus || criteria?.myRating);
}

/** Library page search for the saved criteria of a smart shelf ("Open in library"). */
export function criteriaToSearch(criteria: SmartShelfCriteria): BookSearch {
  return bookSearchSchema.parse(criteria);
}
